/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { HeaderCell } from "@/components/ui/table";
import { SortConfig } from "@/hooks/useTableServer";
import {
  IEnquiryEntry,
  LeadFeedbackStatus,
} from "@/utilities/types/EnquiryEntry";
import { ActionIcon, Text, Tooltip } from "rizzui";
import formatPhone from "@/utilities/functions/format-phone";
import cn from "@/utils/class-names";
import { BiSolidDislike, BiSolidLike } from "react-icons/bi";

type FeedbackSortBy = "project" | "createdAt" | "name";

export type FeedbackColumns = {
  data: IEnquiryEntry[];
  sortConfig?: SortConfig<FeedbackSortBy>;
  isSubmitting: boolean;
  onHeaderCellClick?: (value: string) => void;
  onFeedback: (id: string, type: LeadFeedbackStatus) => void;
};

export const getFeedbackColumns = ({
  data,
  sortConfig,
  onHeaderCellClick,
  onFeedback,
  isSubmitting,
}: FeedbackColumns) => {
  return [
    {
      title: (
        <HeaderCell
          title="Customer"
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "name"
          }
        />
      ),
      dataIndex: "name",
      key: "name",
      align: "left",
      onHeaderCell: () => onHeaderCellClick?.("name"),
      render: (_: unknown, row: IEnquiryEntry) => {
        return (
          <div>
            <Text className="text-sm font-medium text-gray-900 dark:text-gray-700">
              {row.name}
            </Text>
            {row.email && (
              <Text className="text-[13px] text-gray-500">{row.email}</Text>
            )}
            {row?.phone && (
              <Text className="text-[13px] text-gray-500">
                {formatPhone(row.phone)}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      dataIndex: "project",
      key: "project",
      title: (
        <HeaderCell
          title={<span className="whitespace-nowrap">Project</span>}
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "project"
          }
        />
      ),
      onHeaderCell: () => onHeaderCellClick?.("project"),
      render: (_: unknown, row: IEnquiryEntry) => {
        return (
          <div>
            <Text className="text-sm font-medium text-gray-900 dark:text-gray-700">
              {typeof row?.project?.name === "object"
                ? row?.project?.name?.en
                : row?.project?.name}
            </Text>
            {row?.project?.address?.en && (
              <Text className="text-[13px] text-gray-500">
                {row?.project?.address?.en}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <HeaderCell
          title={<span className="whitespace-nowrap">Feedback</span>}
          align="center"
        />
      ),
      dataIndex: "feedback",
      key: "feedback",
      width: 150,
      align: "center",
      render: (_: string, row: IEnquiryEntry) => {
        return (
          <div className="flex items-center justify-center gap-2">
            <Tooltip size="sm" content="Success" placement="top" color="invert">
              <ActionIcon
                size="lg"
                variant="text"
                className="hover:!border-green-500 hover:!bg-green-50 hover:!text-green-600 flex flex-col items-center gap-1 h-auto py-2 border-green-500 text-green-600"
                onClick={() => {
                  if (row.feedbackStatus === "good") return;
                  onFeedback(row.id, "good");
                }}
                disabled={isSubmitting}
              >
                <BiSolidLike size={24} />
                <Text className="text-xs font-semibold">Success</Text>
              </ActionIcon>
            </Tooltip>
            <Tooltip size="sm" content="Failed" placement="top" color="invert">
              <ActionIcon
                size="lg"
                variant="text"
                className="hover:!border-red-500 hover:!bg-red-50 hover:!text-red-600 flex flex-col items-center gap-1 h-auto py-2 border-red-500 text-red-600"
                onClick={() => {
                  if (row.feedbackStatus === "bad") return;
                  onFeedback(row.id, "bad");
                }}
                disabled={isSubmitting}
              >
                <BiSolidDislike size={24} />
                <Text className="text-xs font-semibold">Failed</Text>
              </ActionIcon>
            </Tooltip>
          </div>
        );
      },
    },
  ];
};
